import { affirmSingleton } from "@ardoq/stub-implementation";
import { jsxs, jsx } from "react/jsx-runtime";
import { usePreventScroll } from "@react-aria/overlays";
import styled from "styled-components";
import { zIndex } from "@ardoq/design-tokens";
import { useOnMount, useEffectOnce } from "@ardoq/hooks";
import React, { useState } from "react";
import { v4 } from "uuid";
import { FocusScope } from "@react-aria/focus";
import { last, noop } from "lodash";
const MainWrapper = styled.div`
  z-index: ${zIndex.STACK_PAGE_MANAGER};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: ${({ $isStackActive }) => $isStackActive ? "block" : "none"};
`;
const StackItemWrapper = styled.div.attrs({ tabIndex: -1 })`
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  position: absolute;
  z-index: 1;
`;
var StackPageFunctionName = /* @__PURE__ */ ((StackPageFunctionName2) => {
  StackPageFunctionName2["POP_STACK_PAGE_BY_ID"] = "popStackPageById";
  StackPageFunctionName2["POP_STACK_PAGE_BY_NAME"] = "popStackPageByName";
  StackPageFunctionName2["PUSH_STACK_PAGE"] = "pushStackPage";
  return StackPageFunctionName2;
})(StackPageFunctionName || {});
const stackReducer = (state, action) => {
  switch (action.type) {
    case StackPageFunctionName.POP_STACK_PAGE_BY_ID:
      return {
        ...state,
        stack: state.stack.filter(({ id }) => id !== action.id)
      };
    case StackPageFunctionName.POP_STACK_PAGE_BY_NAME:
      return {
        ...state,
        stack: state.stack.filter(
          ({ stackPageName }) => stackPageName !== action.stackPageName
        )
      };
    case StackPageFunctionName.PUSH_STACK_PAGE:
      return {
        ...state,
        stack: [
          ...state.stack,
          {
            id: v4(),
            StackPage: action.page,
            ...action.options || {}
          }
        ]
      };
  }
};
let _subscriberStack = [];
let _state = { stack: [] };
const dispatch = (action) => {
  _state = stackReducer(_state, action);
  _subscriberStack.forEach(({ subscriber }) => subscriber(_state));
};
const subscribe = (subscriber) => {
  const id = Symbol();
  _subscriberStack.push({ id, subscriber });
  return id;
};
const unsubscribe = (id) => {
  _subscriberStack = _subscriberStack.filter(
    (subscriber) => subscriber.id !== id
  );
};
const getStackPageFunctions = (dispatch2) => ({
  [StackPageFunctionName.POP_STACK_PAGE_BY_ID]: (id) => {
    dispatch2({
      type: StackPageFunctionName.POP_STACK_PAGE_BY_ID,
      id
    });
  },
  [StackPageFunctionName.POP_STACK_PAGE_BY_NAME]: (stackPageName) => {
    dispatch2({
      type: StackPageFunctionName.POP_STACK_PAGE_BY_NAME,
      stackPageName
    });
  },
  [StackPageFunctionName.PUSH_STACK_PAGE]: (page, options = {}) => {
    dispatch2({
      type: StackPageFunctionName.PUSH_STACK_PAGE,
      page,
      options
    });
  }
});
const stackPageFunctions = getStackPageFunctions(dispatch);
const popStackPageById = stackPageFunctions.popStackPageById;
const popStackPageByName = stackPageFunctions.popStackPageByName;
const pushStackPage = stackPageFunctions.pushStackPage;
const stackPageFunctions$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  popStackPageById,
  popStackPageByName,
  pushStackPage
}, Symbol.toStringTag, { value: "Module" }));
const useStackPageManager = () => {
  const [stack, setStack] = useState([]);
  useOnMount(() => {
    const subscriptionId = subscribe(({ stack: stack2 }) => setStack(stack2));
    return () => unsubscribe(subscriptionId);
  });
  return { stack, ...stackPageFunctions$1 };
};
const GenericSelectMenuPortalRootContext = React.createContext(null);
const GenericSelectMenuPortalRootContextProvider = ({
  children,
  active = false
}) => {
  const [element, setElement] = useState(null);
  return /* @__PURE__ */ jsxs(GenericSelectMenuPortalRootContext.Provider, { value: element, children: [
    children,
    active ? /* @__PURE__ */ jsx("div", { ref: setElement, tabIndex: -1, "data-test-id": "menu-portal-root" }) : null
  ] });
};
let externalResolve = noop;
const mountedPromise = new Promise((resolve) => {
  externalResolve = resolve;
});
const getIsMounted = () => mountedPromise;
const StackPageManager = () => {
  var _a;
  const { stack, popStackPageById: popStackPageById2 } = useStackPageManager();
  usePreventScroll({ isDisabled: !stack.length });
  const lastStackId = (_a = last(stack)) == null ? void 0 : _a.id;
  const isStackActive = Boolean(stack.length);
  const getIsPageActive = (pageId) => lastStackId === pageId;
  useEffectOnce(externalResolve);
  return /* @__PURE__ */ jsx(MainWrapper, { $isStackActive: isStackActive, children: stack.map(({ StackPage, id, ...restStackPageOptions }, index) => /* @__PURE__ */ jsx(
    FocusScope,
    {
      contain: getIsPageActive(id),
      restoreFocus: true,
      autoFocus: true,
      children: /* @__PURE__ */ jsx(
        GenericSelectMenuPortalRootContextProvider,
        {
          active: getIsPageActive(id),
          children: /* @__PURE__ */ jsx(StackItemWrapper, { children: /* @__PURE__ */ jsx(
            StackPage,
            {
              stackPageIndex: index,
              stackPageId: id,
              closeStackPage: () => popStackPageById2(id),
              stackPageIsInFocus: getIsPageActive(id),
              ...restStackPageOptions
            }
          ) })
        }
      )
    },
    id
  )) });
};
affirmSingleton("@ardoq/stack-page-manager");
export {
  GenericSelectMenuPortalRootContext,
  StackPageFunctionName,
  StackPageManager,
  getIsMounted,
  popStackPageById,
  popStackPageByName,
  pushStackPage,
  subscribe,
  unsubscribe,
  useStackPageManager
};
//# sourceMappingURL=index.js.map
